import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  User, Building, DollarSign, Percent, Upload, Send, Save, Calculator,
  Users, ArrowRight, FileText, CheckCircle, AlertCircle, MessageSquare,
  FileUp, X, Info, CreditCard, Landmark, Calendar, Phone, Mail, Hash,
  Home, Receipt, Banknote, CheckCheck, Clock, ChevronLeft, ChevronRight,
  Shield, Briefcase, UserCheck, MapPin,
} from 'lucide-react';

const DOCUMENT_TYPES = [
  { key: 'signed_bank_lo', label: 'Bank Letter of Offer', required: true, icon: Landmark },
  { key: 'signed_spa', label: 'Signed SPA', required: true, icon: FileText },
  { key: 'sales_form', label: 'Sales Form', required: true, icon: Receipt },
  { key: 'booking_fee_slip', label: 'Booking Fee Slip', required: false, icon: CreditCard },
  { key: 'downpayment_slip', label: 'Downpayment Slip', required: false, icon: Banknote },
  { key: 'seal', label: 'Company Seal/Stamp', required: false, icon: CheckCheck },
  { key: 'others', label: 'Other Documents', required: false, icon: FileUp },
];

const STEPS = [
  { id: 1, label: 'Entity & Booking', icon: Building },
  { id: 2, label: 'Property & Transaction', icon: Home },
  { id: 3, label: 'Commission Split', icon: DollarSign },
  { id: 4, label: 'Documents', icon: Upload },
  { id: 5, label: 'Declaration', icon: Shield },
];

const RANK_COLORS = {
  evp: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', badge: 'bg-red-600' },
  svp: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', badge: 'bg-amber-600' },
  avp: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', badge: 'bg-blue-600' },
  rm: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', badge: 'bg-purple-600' },
  neg: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', badge: 'bg-emerald-600' },
  customer: { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-200', badge: 'bg-teal-600' },
  introducer: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', badge: 'bg-orange-600' },
  upline: { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-200', badge: 'bg-indigo-600' },
};

const CommissionClaimForm = ({ bookingId, existingClaim, onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [managementChain, setManagementChain] = useState([]);
  const [claimId, setClaimId] = useState(null);
  const [revisionComments, setRevisionComments] = useState([]);
  const [cobrokeChain, setCobrokeChain] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [declarations, setDeclarations] = useState({ accurate: false, commission: false, documents: false });
  const [picUsers, setPicUsers] = useState([]);
  const fileInputRefs = useRef({});

  const [formData, setFormData] = useState({
    entity: 'sam_rumah',
    booking_id: bookingId || '',
    customer_full_name: '',
    customer_contact_no: '',
    customer_email: '',
    customer_nric: '',
    booking_date: '',
    signing_date: '',
    project_name: '',
    lot_status: 'non_bumi',
    unit_number: '',
    spa_price: 0,
    nett_price: 0,
    bank_name: '',
    loan_amount: 0,
    loan_type: 'conventional',
    commission_percentage: 0,
    status_checklist: [],
    full_loan: false,
    cash_out: 0,
    downpayment: 0,
    remark: '',
    is_cobroke: false,
    // SP SAM specific
    pic_commission_percentage: 2,
    pic_commission_amount: 0,
    pic_user_id: '',
    pic_user_name: '',
    trip_wallet_percentage: 1,
    trip_wallet_amount: 0,
    // SST
    sst_percentage: 8,
    total_sst_amount: 0,
    // Platform fee (SAM Rumah only)
    platform_fee_percentage: 5,
    platform_fee_amount: 0,
  });

  const isEditing = !!existingClaim;
  const isRevision = existingClaim?.status === 'revision_requested';
  const isSamRumah = formData.entity === 'sam_rumah';
  const totalGrossComm = formData.spa_price * (formData.commission_percentage / 100);

  // Load existing claim data
  useEffect(() => {
    if (existingClaim) {
      setClaimId(existingClaim.id);
      setFormData(prev => ({
        ...prev,
        entity: existingClaim.entity || 'sam_rumah',
        booking_id: existingClaim.booking_id || '',
        customer_full_name: existingClaim.customer_full_name || '',
        customer_contact_no: existingClaim.customer_contact_no || '',
        customer_email: existingClaim.customer_email || '',
        customer_nric: existingClaim.customer_nric || '',
        booking_date: existingClaim.booking_date || '',
        signing_date: existingClaim.signing_date || '',
        project_name: existingClaim.project_name || '',
        lot_status: existingClaim.lot_status || 'non_bumi',
        unit_number: existingClaim.unit_number || '',
        spa_price: existingClaim.spa_price || 0,
        nett_price: existingClaim.nett_price || 0,
        bank_name: existingClaim.bank_name || '',
        loan_amount: existingClaim.loan_amount || 0,
        loan_type: existingClaim.loan_type || 'conventional',
        commission_percentage: existingClaim.commission_percentage || 0,
        status_checklist: existingClaim.status_checklist || [],
        full_loan: existingClaim.full_loan || false,
        cash_out: existingClaim.cash_out || 0,
        downpayment: existingClaim.downpayment || 0,
        remark: existingClaim.remark || '',
        is_cobroke: existingClaim.is_cobroke || false,
        pic_commission_percentage: existingClaim.pic_commission_percentage || 2,
        pic_commission_amount: existingClaim.pic_commission_amount || 0,
        pic_user_id: existingClaim.pic_user_id || '',
        pic_user_name: existingClaim.pic_user_name || '',
        trip_wallet_percentage: existingClaim.trip_wallet_percentage || 1,
        trip_wallet_amount: existingClaim.trip_wallet_amount || 0,
        sst_percentage: existingClaim.sst_percentage || 8,
        total_sst_amount: existingClaim.total_sst_amount || 0,
        platform_fee_percentage: existingClaim.platform_fee_percentage || 5,
        platform_fee_amount: existingClaim.platform_fee_amount || 0,
      }));
      if (existingClaim.management_recipients?.length > 0) setManagementChain(existingClaim.management_recipients);
      if (existingClaim.cobroke_recipients?.length > 0) setCobrokeChain(existingClaim.cobroke_recipients);
      if (existingClaim.documents) setUploadedDocs(existingClaim.documents);
      if (existingClaim.revision_requests?.length > 0) setRevisionComments(existingClaim.revision_requests);
    }
  }, [existingClaim]);

  useEffect(() => { loadBookings(); loadPicUsers(); }, []);

  useEffect(() => {
    if (user?.id && !isEditing) loadManagementChain(user.id);
  }, [user, isEditing]);

  const loadBookings = async () => {
    try {
      const res = await api.get('/bookings');
      setBookings(res.data.filter(b => ['spa_signed', 'completed'].includes(b.status)));
    } catch (error) { console.error('Error loading bookings:', error); }
  };

  const loadPicUsers = async () => {
    try {
      const res = await api.get('/commission-claims/helpers/pic-users');
      setPicUsers(Array.isArray(res.data) ? res.data : []);
    } catch (error) { console.error('Error loading PIC users:', error); }
  };

  const loadManagementChain = async (agentId) => {
    try {
      const res = await api.get(`/commission-claims/helpers/agent-upline/${agentId}`);
      const chain = res.data.upline_chain || [];
      setManagementChain(chain.map(member => ({
        ...member,
        rate_percentage: member.rate_percentage || 0,
        gross_comm: 0, sst_amount: 0, after_sst: 0, platform_fee: 0, nett_comm: 0, balance_comm: 0,
      })));
    } catch (error) { console.error('Error loading management chain:', error); }
  };

  const loadCobrokeChain = async (customerId) => {
    try {
      const res = await api.get(`/commission-claims/helpers/cobroke-chain/${customerId}`);
      const chain = res.data.cobroke_chain || [];
      setCobrokeChain(chain.map(member => ({
        ...member,
        rate_percentage: member.rate_percentage || 0,
        gross_comm: 0, sst_amount: 0, after_sst: 0, platform_fee: 0, nett_comm: 0, balance_comm: 0,
      })));
    } catch (error) { setCobrokeChain([]); }
  };

  const handleBookingSelect = (bId) => {
    const booking = bookings.find(b => b.id === bId);
    if (booking) {
      setSelectedBooking(booking);
      const propertyName = booking.property?.name || booking.property_name || '';
      const unitNumber = booking.unit?.unit_number || booking.manual_unit_number || booking.unit_number || '';
      const unitPrice = booking.unit?.price || booking.unit_price || booking.booking_fee_amount || 0;
      const commissionPct = booking.property?.commission_percentage || booking.commission_percentage || 3;
      setFormData(prev => ({
        ...prev,
        booking_id: booking.id,
        customer_full_name: booking.buyer_name || booking.form_data?.buyer_name || '',
        customer_contact_no: booking.buyer_phone || booking.form_data?.buyer_phone || '',
        customer_email: booking.buyer_email || booking.form_data?.buyer_email || '',
        customer_nric: booking.buyer_ic || booking.form_data?.buyer_ic || '',
        project_name: propertyName,
        unit_number: unitNumber,
        spa_price: unitPrice,
        nett_price: unitPrice,
        commission_percentage: commissionPct,
        booking_date: booking.created_at?.split('T')[0] || '',
      }));
      if (booking.buyer_id && isSamRumah) loadCobrokeChain(booking.buyer_id);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // 3-Part Commission Formula per recipient:
  // Part 1: Gross = Pool × Rate% → SST = Gross × 8% → Balance = Gross - SST
  // Part 2: Platform Fee = Balance × 5% (SAM Rumah only) → Net = Balance - Platform Fee
  // Part 3: Fast Comm = Net × 20% (if applicable) → Final = Net - Fast Comm
  const calcRecipient = useCallback((ratePct) => {
    const gross = totalGrossComm * ((ratePct || 0) / 100);
    const sst = gross * (formData.sst_percentage / 100);
    const balance = gross - sst;
    const platformFee = isSamRumah ? balance * ((formData.platform_fee_percentage || 0) / 100) : 0;
    const nett = balance - platformFee;
    return { gross_comm: gross, sst_amount: sst, balance_comm: balance, platform_fee: platformFee, nett_comm: nett };
  }, [totalGrossComm, formData.sst_percentage, formData.platform_fee_percentage, isSamRumah]);

  // Recalculate commissions when key fields change
  const recalculateCommissions = useCallback(() => {
    if (totalGrossComm <= 0) return;

    // Recalc management chain with 3-part formula
    setManagementChain(prev => prev.map(m => ({
      ...m,
      ...calcRecipient(m.rate_percentage),
    })));

    // Recalc cobroke chain (SAM Rumah only)
    if (isSamRumah) {
      setCobrokeChain(prev => prev.map(m => ({
        ...m,
        ...calcRecipient(m.rate_percentage),
      })));
    }

    // Recalc SP SAM specific
    if (!isSamRumah) {
      const picCalc = calcRecipient(formData.pic_commission_percentage);
      const tripCalc = calcRecipient(formData.trip_wallet_percentage);
      setFormData(prev => ({
        ...prev,
        pic_commission_amount: picCalc.nett_comm,
        trip_wallet_amount: tripCalc.nett_comm,
      }));
    }
  }, [totalGrossComm, isSamRumah, calcRecipient, formData.pic_commission_percentage, formData.trip_wallet_percentage]);

  useEffect(() => { recalculateCommissions(); }, [formData.spa_price, formData.commission_percentage, formData.entity, formData.sst_percentage, formData.platform_fee_percentage]);

  const updateManagementCommission = (index, field, value) => {
    setManagementChain(prev => {
      const updated = [...prev];
      const numValue = parseFloat(value) || 0;
      if (field === 'rate_percentage') {
        updated[index] = { ...updated[index], rate_percentage: numValue, ...calcRecipient(numValue) };
      } else if (field === 'gross_comm') {
        const rate = totalGrossComm > 0 ? (numValue / totalGrossComm) * 100 : 0;
        updated[index] = { ...updated[index], rate_percentage: rate, ...calcRecipient(rate) };
      }
      return updated;
    });
  };

  const updateCobrokeCommission = (index, field, value) => {
    setCobrokeChain(prev => {
      const updated = [...prev];
      const numValue = parseFloat(value) || 0;
      if (field === 'rate_percentage') {
        updated[index] = { ...updated[index], rate_percentage: numValue, ...calcRecipient(numValue) };
      } else if (field === 'gross_comm') {
        const rate = totalGrossComm > 0 ? (numValue / totalGrossComm) * 100 : 0;
        updated[index] = { ...updated[index], rate_percentage: rate, ...calcRecipient(rate) };
      }
      return updated;
    });
  };

  // Totals (using nett_comm which already has SST + platform fee deducted per recipient)
  const totalAgentGross = managementChain.reduce((sum, r) => sum + (r.gross_comm || 0), 0);
  const totalAgentSST = managementChain.reduce((sum, r) => sum + (r.sst_amount || 0), 0);
  const totalAgentPlatform = managementChain.reduce((sum, r) => sum + (r.platform_fee || 0), 0);
  const totalAgentNett = managementChain.reduce((sum, r) => sum + (r.nett_comm || 0), 0);

  const totalCobrokeGross = cobrokeChain.reduce((sum, r) => sum + (r.gross_comm || 0), 0);
  const totalCobrokeSST = cobrokeChain.reduce((sum, r) => sum + (r.sst_amount || 0), 0);
  const totalCobrokePlatform = cobrokeChain.reduce((sum, r) => sum + (r.platform_fee || 0), 0);
  const totalCobrokeNett = cobrokeChain.reduce((sum, r) => sum + (r.nett_comm || 0), 0);

  const picCalcResult = !isSamRumah ? calcRecipient(formData.pic_commission_percentage) : { gross_comm: 0, sst_amount: 0, nett_comm: 0 };
  const tripCalcResult = !isSamRumah ? calcRecipient(formData.trip_wallet_percentage) : { gross_comm: 0, sst_amount: 0, nett_comm: 0 };

  const grandTotalGross = totalAgentGross + (isSamRumah ? totalCobrokeGross : picCalcResult.gross_comm + tripCalcResult.gross_comm);
  const grandTotalSST = totalAgentSST + (isSamRumah ? totalCobrokeSST : picCalcResult.sst_amount + tripCalcResult.sst_amount);
  const grandTotalNett = totalAgentNett + (isSamRumah ? totalCobrokeNett : picCalcResult.nett_comm + tripCalcResult.nett_comm);
  const remainingPool = totalGrossComm - grandTotalGross;

  // File upload
  const handleFileUpload = async (docType, file) => {
    if (!claimId) { toast.error('Please save as draft first before uploading documents'); return; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('document_type', docType);
    setUploadProgress(prev => ({ ...prev, [docType]: 0 }));
    try {
      const res = await api.post(`/commission-claims/${claimId}/upload-document`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadProgress(prev => ({ ...prev, [docType]: Math.round((e.loaded * 100) / e.total) })),
      });
      setUploadedDocs(prev => ({
        ...prev,
        [docType]: [...(prev[docType] || []), { filename: res.data.filename, original_name: file.name, uploaded_at: new Date().toISOString() }],
      }));
      toast.success(`${file.name} uploaded`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Upload failed'); }
    finally { setUploadProgress(prev => ({ ...prev, [docType]: undefined })); }
  };

  const handleFileSelect = (docType) => (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(docType, file);
    e.target.value = '';
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEditing && claimId) {
        await api.put(`/commission-claims/${claimId}`, payload);
        toast.success('Claim updated');
      } else {
        const res = await api.post('/commission-claims', payload);
        setClaimId(res.data.id);
        toast.success('Claim saved as draft');
      }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleSubmit = async () => {
    if (!formData.booking_id && !isEditing) { toast.error('Please select a booking'); return; }
    if (!formData.customer_full_name) { toast.error('Customer name is required'); return; }
    if (formData.commission_percentage <= 0) { toast.error('Commission % is required'); return; }
    if (!declarations.accurate || !declarations.commission || !declarations.documents) {
      toast.error('Please accept all declarations'); return;
    }

    setLoading(true);
    try {
      const payload = buildPayload();
      let targetClaimId = claimId;
      if (isEditing && claimId) {
        await api.put(`/commission-claims/${claimId}`, payload);
      } else {
        const createRes = await api.post('/commission-claims', payload);
        targetClaimId = createRes.data.id;
      }
      await api.post(`/commission-claims/${targetClaimId}/submit`);
      toast.success(isRevision ? 'Claim resubmitted for review' : 'Claim submitted for review');
      if (onSuccess) onSuccess();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to submit'); }
    finally { setLoading(false); }
  };

  const buildPayload = () => ({
    ...formData,
    management_recipients: managementChain,
    cobroke_recipients: isSamRumah && formData.is_cobroke ? cobrokeChain : [],
    total_sst_amount: grandTotalSST,
    platform_fee_amount: isSamRumah ? (totalAgentPlatform + totalCobrokePlatform) : 0,
    pic_commission_amount: !isSamRumah ? picCalcResult.nett_comm : 0,
    trip_wallet_amount: !isSamRumah ? tripCalcResult.nett_comm : 0,
  });

  const getStepCompletion = (step) => {
    switch (step) {
      case 1: return formData.entity && formData.booking_id ? 100 : formData.entity ? 50 : 0;
      case 2: {
        const fields = ['customer_full_name', 'customer_nric', 'project_name', 'unit_number', 'spa_price', 'commission_percentage'];
        return Math.round((fields.filter(f => formData[f]).length / fields.length) * 100);
      }
      case 3: return managementChain.some(m => m.rate_percentage > 0) ? 100 : 0;
      case 4: {
        const requiredDocs = DOCUMENT_TYPES.filter(d => d.required);
        const uploaded = requiredDocs.filter(d => (uploadedDocs[d.key] || []).length > 0);
        return requiredDocs.length > 0 ? Math.round((uploaded.length / requiredDocs.length) * 100) : 100;
      }
      case 5: return declarations.accurate && declarations.commission && declarations.documents ? 100 : 0;
      default: return 0;
    }
  };

  const overallCompletion = Math.round(STEPS.reduce((sum, s) => sum + getStepCompletion(s.id), 0) / STEPS.length);

  const getRankColor = (rank) => RANK_COLORS[rank?.toLowerCase()] || RANK_COLORS.neg;

  // =================== RENDER ===================
  return (
    <div className="space-y-6" data-testid="commission-claim-form">
      {/* Revision Banner */}
      {isRevision && revisionComments.length > 0 && (
        <Card className="border-orange-300 bg-orange-50" data-testid="revision-banner">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
              <MessageSquare className="h-5 w-5" />
              Revision Required — Please Address the Following
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {revisionComments.map((rev, idx) => (
              <div key={idx} className="p-3 bg-white rounded-lg border border-orange-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-orange-700 border-orange-300">{rev.requested_by_role?.toUpperCase()}</Badge>
                    <span className="text-sm font-medium">{rev.requested_by_name}</span>
                  </div>
                  <span className="text-xs text-gray-500">{formatDate(rev.requested_at)}</span>
                </div>
                {rev.general_comment && <p className="text-sm text-gray-700 mb-2">{rev.general_comment}</p>}
                {rev.field_comments && Object.keys(rev.field_comments).length > 0 && (
                  <div className="space-y-1 text-sm">
                    <p className="font-medium text-orange-700">Field-specific comments:</p>
                    {Object.entries(rev.field_comments).map(([field, comment]) => (
                      <div key={field} className="flex gap-2 pl-2">
                        <span className="font-medium capitalize text-gray-600">{field.replace(/_/g, ' ')}:</span>
                        <span className="text-red-600">{comment}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Step Progress Indicator */}
      <Card className="bg-gradient-to-r from-slate-50 to-blue-50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">Overall Progress</span>
            <span className="text-sm font-bold text-blue-600">{overallCompletion}%</span>
          </div>
          <Progress value={overallCompletion} className="h-2 mb-4" />
          <div className="flex items-center justify-between" data-testid="step-indicators">
            {STEPS.map((step, idx) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = getStepCompletion(step.id) === 100;
              return (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => setCurrentStep(step.id)}
                    className={`flex flex-col items-center gap-1.5 transition-all ${isActive ? 'scale-105' : ''}`}
                    data-testid={`step-${step.id}-btn`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' :
                      isCompleted ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {isCompleted && !isActive ? <CheckCircle className="h-5 w-5" /> : <StepIcon className="h-5 w-5" />}
                    </div>
                    <span className={`text-xs font-medium ${isActive ? 'text-blue-600' : 'text-gray-500'}`}>
                      {step.label}
                    </span>
                  </button>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 ${getStepCompletion(step.id) === 100 ? 'bg-emerald-400' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ====== STEP 1: Entity & Booking ====== */}
      {currentStep === 1 && (
        <div className="space-y-4" data-testid="step-1-content">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Briefcase className="h-5 w-5" /> Select Entity</CardTitle>
              <CardDescription>Choose the business entity for this commission claim</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleInputChange('entity', 'sam_rumah')}
                  className={`p-6 rounded-xl border-2 transition-all text-left ${
                    formData.entity === 'sam_rumah'
                      ? 'border-emerald-500 bg-emerald-50 shadow-lg shadow-emerald-100'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                  data-testid="entity-sam-rumah"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${
                      formData.entity === 'sam_rumah' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'
                    }`}>SR</div>
                    <div>
                      <p className="font-semibold text-base">SAM Rumah</p>
                      <p className="text-xs text-gray-500">Customer Cashback Entity</p>
                    </div>
                  </div>
                  <div className="space-y-1 text-xs text-gray-600">
                    <p>&#x2022; Customer cashback hierarchy</p>
                    <p>&#x2022; Agent commission hierarchy</p>
                    <p>&#x2022; Platform fees apply</p>
                  </div>
                </button>

                <button
                  onClick={() => handleInputChange('entity', 'sp_sam')}
                  className={`p-6 rounded-xl border-2 transition-all text-left ${
                    formData.entity === 'sp_sam'
                      ? 'border-blue-500 bg-blue-50 shadow-lg shadow-blue-100'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                  data-testid="entity-sp-sam"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${
                      formData.entity === 'sp_sam' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
                    }`}>SP</div>
                    <div>
                      <p className="font-semibold text-base">SP SAM Property</p>
                      <p className="text-xs text-gray-500">Direct Commission Entity</p>
                    </div>
                  </div>
                  <div className="space-y-1 text-xs text-gray-600">
                    <p>&#x2022; Agent commission hierarchy</p>
                    <p>&#x2022; PIC commission allocation</p>
                    <p>&#x2022; Trip wallet deduction</p>
                  </div>
                </button>
              </div>
            </CardContent>
          </Card>

          {!isEditing && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5" /> Select Booking</CardTitle>
                <CardDescription>Select a completed booking to create a commission claim</CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={formData.booking_id} onValueChange={handleBookingSelect}>
                  <SelectTrigger data-testid="booking-select"><SelectValue placeholder="Select a booking..." /></SelectTrigger>
                  <SelectContent>
                    {bookings.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">No claimable bookings found</div>
                    ) : bookings.map(booking => (
                      <SelectItem key={booking.id} value={booking.id}>
                        <div className="flex items-center gap-2">
                          <Home className="h-4 w-4 text-gray-400" />
                          <span>{booking.property_name} - {booking.unit_number}</span>
                          <Badge variant="outline" className="ml-2">{booking.buyer_name}</Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          )}

          {isEditing && existingClaim && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Editing Claim</p>
                    <p className="font-semibold text-blue-700">{existingClaim.claim_ref}</p>
                  </div>
                  <Badge className={formData.entity === 'sam_rumah' ? 'bg-emerald-500' : 'bg-blue-500'}>
                    {formData.entity === 'sam_rumah' ? 'SAM Rumah' : 'SP SAM'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ====== STEP 2: Property & Transaction ====== */}
      {currentStep === 2 && (
        <div className="space-y-4" data-testid="step-2-content">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><User className="h-5 w-5" /> Buyer Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><User className="h-4 w-4 text-gray-400" /> Full Name *</Label>
                <Input data-testid="customer-name-input" value={formData.customer_full_name} onChange={(e) => handleInputChange('customer_full_name', e.target.value)} placeholder="Buyer full name" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Hash className="h-4 w-4 text-gray-400" /> NRIC *</Label>
                <Input data-testid="customer-nric-input" value={formData.customer_nric} onChange={(e) => handleInputChange('customer_nric', e.target.value)} placeholder="e.g., 880101-12-1234" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Phone className="h-4 w-4 text-gray-400" /> Contact Number *</Label>
                <Input data-testid="customer-phone-input" value={formData.customer_contact_no} onChange={(e) => handleInputChange('customer_contact_no', e.target.value)} placeholder="+60123456789" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Mail className="h-4 w-4 text-gray-400" /> Email *</Label>
                <Input data-testid="customer-email-input" type="email" value={formData.customer_email} onChange={(e) => handleInputChange('customer_email', e.target.value)} placeholder="buyer@email.com" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Building className="h-5 w-5" /> Property Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Project Name *</Label>
                  <Input data-testid="project-name-input" value={formData.project_name} onChange={(e) => handleInputChange('project_name', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Unit Number *</Label>
                  <Input data-testid="unit-number-input" value={formData.unit_number} onChange={(e) => handleInputChange('unit_number', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Lot Status *</Label>
                  <Select value={formData.lot_status} onValueChange={(v) => handleInputChange('lot_status', v)}>
                    <SelectTrigger data-testid="lot-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bumi">Bumi</SelectItem>
                      <SelectItem value="non_bumi">Non-Bumi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><DollarSign className="h-4 w-4 text-gray-400" /> SPA Price (RM) *</Label>
                  <Input data-testid="spa-price-input" type="number" value={formData.spa_price || ''} onChange={(e) => handleInputChange('spa_price', parseFloat(e.target.value) || 0)} />
                </div>
                <div className="space-y-2">
                  <Label>Nett Price (RM)</Label>
                  <Input type="number" value={formData.nett_price || ''} onChange={(e) => handleInputChange('nett_price', parseFloat(e.target.value) || 0)} />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Percent className="h-4 w-4 text-gray-400" /> Commission Rate (%) *</Label>
                  <Input data-testid="commission-pct-input" type="number" step="0.01" value={formData.commission_percentage || ''} onChange={(e) => handleInputChange('commission_percentage', parseFloat(e.target.value) || 0)} />
                </div>
              </div>

              {totalGrossComm > 0 && (
                <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Calculator className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-blue-800">Commission Pool</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        {formatCurrency(formData.spa_price)} x {formData.commission_percentage}%
                      </span>
                      <span className="text-2xl font-bold text-blue-700" data-testid="total-commission-pool">{formatCurrency(totalGrossComm)}</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Landmark className="h-4 w-4 text-gray-400" /> Bank Name</Label>
                  <Input value={formData.bank_name} onChange={(e) => handleInputChange('bank_name', e.target.value)} placeholder="e.g., Maybank" />
                </div>
                <div className="space-y-2">
                  <Label>Loan Amount (RM)</Label>
                  <Input type="number" value={formData.loan_amount || ''} onChange={(e) => handleInputChange('loan_amount', parseFloat(e.target.value) || 0)} />
                </div>
                <div className="space-y-2">
                  <Label>Loan Type</Label>
                  <Select value={formData.loan_type} onValueChange={(v) => handleInputChange('loan_type', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conventional">Conventional</SelectItem>
                      <SelectItem value="islamic">Islamic</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Calendar className="h-4 w-4 text-gray-400" /> Booking Date</Label>
                  <Input type="date" value={formData.booking_date} onChange={(e) => handleInputChange('booking_date', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Calendar className="h-4 w-4 text-gray-400" /> SPA Signing Date *</Label>
                  <Input type="date" value={formData.signing_date} onChange={(e) => handleInputChange('signing_date', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Downpayment (RM)</Label>
                  <Input type="number" value={formData.downpayment || ''} onChange={(e) => handleInputChange('downpayment', parseFloat(e.target.value) || 0)} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="full_loan" checked={formData.full_loan} onCheckedChange={(c) => handleInputChange('full_loan', c)} />
                <Label htmlFor="full_loan">Full Loan (100%)</Label>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ====== STEP 3: Commission Distribution ====== */}
      {currentStep === 3 && (
        <div className="space-y-4" data-testid="step-3-content">
          {/* Commission Pool Header */}
          <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total Commission Pool</p>
                  <p className="text-3xl font-bold" data-testid="commission-pool-display">{formatCurrency(totalGrossComm)}</p>
                  <p className="text-blue-200 text-xs mt-1">
                    {formatCurrency(formData.spa_price)} x {formData.commission_percentage}%
                  </p>
                </div>
                <Badge className={isSamRumah ? 'bg-emerald-500 text-white' : 'bg-white text-blue-600'}>
                  {isSamRumah ? 'SAM Rumah' : 'SP SAM'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Section A: Agent Hierarchy */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Users className="h-5 w-5" /> Agent Commission Hierarchy</CardTitle>
              <CardDescription>
                3-Part Formula: Gross &#8594; -SST(8%) &#8594; Balance{isSamRumah ? ' → -Platform(5%) → Net' : ' = Net'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Column headers */}
              <div className="hidden md:grid grid-cols-12 gap-2 mb-2 px-3 text-xs font-semibold text-gray-500 uppercase">
                <div className="col-span-3">Recipient</div>
                <div className="col-span-1 text-right">Rate%</div>
                <div className="col-span-2 text-right">Gross (RM)</div>
                <div className="col-span-2 text-right">SST 8% (RM)</div>
                {isSamRumah && <div className="col-span-2 text-right">Plat 5% (RM)</div>}
                <div className={`${isSamRumah ? 'col-span-2' : 'col-span-4'} text-right`}>Net (RM)</div>
              </div>
              <div className="space-y-2">
                {managementChain.map((member, idx) => {
                  const colors = getRankColor(member.rank);
                  return (
                    <div key={idx} className={`grid grid-cols-12 gap-2 items-center p-2.5 rounded-lg border ${colors.bg} ${colors.border}`}>
                      <div className="col-span-3 flex items-center gap-2 min-w-0">
                        <Badge className={`${colors.badge} text-white text-xs shrink-0`}>
                          {member.rank_display || member.rank?.toUpperCase()}
                        </Badge>
                        <span className="text-xs font-medium truncate">{member.name}</span>
                      </div>
                      <div className="col-span-1">
                        <Input type="number" step="0.1" min="0" max="100" className="w-full text-right h-7 text-xs" value={member.rate_percentage || ''} onChange={(e) => updateManagementCommission(idx, 'rate_percentage', e.target.value)} />
                      </div>
                      <div className="col-span-2 text-right font-mono text-xs font-semibold">{formatCurrency(member.gross_comm || 0)}</div>
                      <div className="col-span-2 text-right font-mono text-xs text-red-600">-{formatCurrency(member.sst_amount || 0)}</div>
                      {isSamRumah && <div className="col-span-2 text-right font-mono text-xs text-amber-600">-{formatCurrency(member.platform_fee || 0)}</div>}
                      <div className={`${isSamRumah ? 'col-span-2' : 'col-span-4'} text-right font-mono text-sm font-bold text-blue-700`}>{formatCurrency(member.nett_comm || 0)}</div>
                    </div>
                  );
                })}
                {managementChain.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>No agent hierarchy found. Contact admin to set up your upline chain.</p>
                  </div>
                )}
              </div>
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-12 gap-2 text-sm">
                  <div className="col-span-4 font-medium text-gray-600">Agent Hierarchy Total</div>
                  <div className="col-span-2 text-right font-mono font-semibold">{formatCurrency(totalAgentGross)}</div>
                  <div className="col-span-2 text-right font-mono text-red-600">-{formatCurrency(totalAgentSST)}</div>
                  {isSamRumah && <div className="col-span-2 text-right font-mono text-amber-600">-{formatCurrency(totalAgentPlatform)}</div>}
                  <div className={`${isSamRumah ? 'col-span-2' : 'col-span-4'} text-right font-mono font-bold text-blue-700`}>{formatCurrency(totalAgentNett)}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section B: SAM Rumah = Customer Cashback / SP SAM = PIC + Trip */}
          {isSamRumah ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2"><ArrowRight className="h-5 w-5" /> Customer Cashback Hierarchy</CardTitle>
                    <CardDescription>Customer &#8594; Introducer &#8594; Upline 1-4 &#8594; Agent</CardDescription>
                  </div>
                  <div className="flex items-center gap-2 bg-emerald-50 p-2 rounded-lg">
                    <Checkbox id="is_cobroke" checked={formData.is_cobroke} onCheckedChange={(c) => handleInputChange('is_cobroke', c)} />
                    <Label htmlFor="is_cobroke" className="text-emerald-700 font-medium text-sm">Enable Cashback</Label>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!formData.is_cobroke ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                    <p className="font-medium">Customer Cashback Not Enabled</p>
                    <p className="text-sm">Enable above if this deal includes customer cashback</p>
                  </div>
                ) : (
                  <>
                    <div className="hidden md:grid grid-cols-12 gap-2 mb-2 px-3 text-xs font-semibold text-gray-500 uppercase">
                      <div className="col-span-3">Recipient</div>
                      <div className="col-span-1 text-right">Rate%</div>
                      <div className="col-span-2 text-right">Gross (RM)</div>
                      <div className="col-span-2 text-right">SST 8% (RM)</div>
                      <div className="col-span-2 text-right">Plat 5% (RM)</div>
                      <div className="col-span-2 text-right">Net (RM)</div>
                    </div>
                    <div className="space-y-2">
                      {cobrokeChain.map((member, idx) => {
                        const colors = getRankColor(member.level || member.rank);
                        return (
                          <div key={idx} className={`grid grid-cols-12 gap-2 items-center p-2.5 rounded-lg border ${colors.bg} ${colors.border}`}>
                            <div className="col-span-3 flex items-center gap-2 min-w-0">
                              <Badge className={`${colors.badge} text-white text-xs shrink-0`}>
                                {member.level_display || member.rank_display}
                              </Badge>
                              <span className="text-xs font-medium truncate">{member.name}</span>
                            </div>
                            <div className="col-span-1">
                              <Input type="number" step="0.1" min="0" max="100" className="w-full text-right h-7 text-xs" value={member.rate_percentage || ''} onChange={(e) => updateCobrokeCommission(idx, 'rate_percentage', e.target.value)} />
                            </div>
                            <div className="col-span-2 text-right font-mono text-xs font-semibold">{formatCurrency(member.gross_comm || 0)}</div>
                            <div className="col-span-2 text-right font-mono text-xs text-red-600">-{formatCurrency(member.sst_amount || 0)}</div>
                            <div className="col-span-2 text-right font-mono text-xs text-amber-600">-{formatCurrency(member.platform_fee || 0)}</div>
                            <div className="col-span-2 text-right font-mono text-sm font-bold text-emerald-700">{formatCurrency(member.nett_comm || 0)}</div>
                          </div>
                        );
                      })}
                      {cobrokeChain.length === 0 && formData.is_cobroke && (
                        <div className="text-center py-6 text-gray-500">
                          <AlertCircle className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm">No customer hierarchy found for this buyer.</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 p-3 bg-emerald-50 rounded-lg">
                      <div className="grid grid-cols-12 gap-2 text-sm">
                        <div className="col-span-4 font-medium text-gray-600">Cashback Hierarchy Total</div>
                        <div className="col-span-2 text-right font-mono font-semibold">{formatCurrency(totalCobrokeGross)}</div>
                        <div className="col-span-2 text-right font-mono text-red-600">-{formatCurrency(totalCobrokeSST)}</div>
                        <div className="col-span-2 text-right font-mono text-amber-600">-{formatCurrency(totalCobrokePlatform)}</div>
                        <div className="col-span-2 text-right font-mono font-bold text-emerald-700">{formatCurrency(totalCobrokeNett)}</div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            /* SP SAM: PIC Commission + Trip Wallet */
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><UserCheck className="h-5 w-5" /> PIC Commission &amp; Trip Wallet</CardTitle>
                <CardDescription>3-Part Formula: Gross &#8594; -SST(8%) &#8594; Net (no platform fee for SP SAM)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg border border-blue-200 bg-blue-50">
                  <div className="flex items-center gap-3 mb-3">
                    <Badge className="bg-blue-600 text-white">PIC</Badge>
                    <span className="font-medium">PIC Commission</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div className="space-y-2">
                      <Label className="text-xs">PIC Person</Label>
                      <Select value={formData.pic_user_id} onValueChange={(v) => {
                        const picUser = picUsers.find(p => p.id === v);
                        handleInputChange('pic_user_id', v);
                        handleInputChange('pic_user_name', picUser?.full_name || '');
                      }}>
                        <SelectTrigger data-testid="pic-select"><SelectValue placeholder="Select PIC..." /></SelectTrigger>
                        <SelectContent>
                          {picUsers.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Rate %</Label>
                      <Input type="number" step="0.1" min="0" max="100" value={formData.pic_commission_percentage || ''} onChange={(e) => handleInputChange('pic_commission_percentage', parseFloat(e.target.value) || 0)} data-testid="pic-pct-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Gross (RM)</Label>
                      <div className="font-mono font-semibold text-sm pt-2">{formatCurrency(picCalcResult.gross_comm)}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-xs">
                    <div><span className="text-gray-500">SST (8%):</span> <span className="font-mono text-red-600">-{formatCurrency(picCalcResult.sst_amount)}</span></div>
                    <div><span className="text-gray-500">Net:</span> <span className="font-mono font-bold text-blue-700">{formatCurrency(picCalcResult.nett_comm)}</span></div>
                  </div>
                </div>

                <div className="p-4 rounded-lg border border-purple-200 bg-purple-50">
                  <div className="flex items-center gap-3 mb-3">
                    <Badge className="bg-purple-600 text-white">TRIP</Badge>
                    <span className="font-medium">Trip Wallet Deduction</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div className="space-y-2">
                      <Label className="text-xs">Rate %</Label>
                      <Input type="number" step="0.1" min="0" max="100" value={formData.trip_wallet_percentage || ''} onChange={(e) => handleInputChange('trip_wallet_percentage', parseFloat(e.target.value) || 0)} data-testid="trip-pct-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Gross (RM)</Label>
                      <div className="font-mono font-semibold text-sm pt-2">{formatCurrency(tripCalcResult.gross_comm)}</div>
                    </div>
                    <div></div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-xs">
                    <div><span className="text-gray-500">SST (8%):</span> <span className="font-mono text-red-600">-{formatCurrency(tripCalcResult.sst_amount)}</span></div>
                    <div><span className="text-gray-500">Net:</span> <span className="font-mono font-bold text-purple-700">{formatCurrency(tripCalcResult.nett_comm)}</span></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Platform Fee (SAM Rumah only) - Note: Already deducted per recipient */}
          {isSamRumah && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-amber-600 text-white">PLATFORM</Badge>
                    <span className="font-medium text-sm">Platform Fee Rate (deducted per recipient above)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number" step="0.1" min="0"
                      className="w-20 text-right h-8 text-sm"
                      value={formData.platform_fee_percentage || ''}
                      onChange={(e) => handleInputChange('platform_fee_percentage', parseFloat(e.target.value) || 0)}
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Grand Total Summary */}
          <Card className="border-2 border-gray-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Calculator className="h-5 w-5" /> Commission Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-4 gap-2 text-xs font-semibold text-gray-500 uppercase px-2">
                <div>Section</div>
                <div className="text-right">Gross</div>
                <div className="text-right">SST (8%)</div>
                <div className="text-right">Net Payable</div>
              </div>
              <Separator />
              <div className="grid grid-cols-4 gap-2 text-sm px-2">
                <div className="text-gray-600">Agent Hierarchy</div>
                <div className="text-right font-mono">{formatCurrency(totalAgentGross)}</div>
                <div className="text-right font-mono text-red-600">-{formatCurrency(totalAgentSST)}</div>
                <div className="text-right font-mono font-semibold text-blue-700">{formatCurrency(totalAgentNett)}</div>
              </div>
              {isSamRumah && formData.is_cobroke && (
                <div className="grid grid-cols-4 gap-2 text-sm px-2">
                  <div className="text-gray-600">Customer Cashback</div>
                  <div className="text-right font-mono">{formatCurrency(totalCobrokeGross)}</div>
                  <div className="text-right font-mono text-red-600">-{formatCurrency(totalCobrokeSST)}</div>
                  <div className="text-right font-mono font-semibold text-emerald-700">{formatCurrency(totalCobrokeNett)}</div>
                </div>
              )}
              {!isSamRumah && (
                <>
                  <div className="grid grid-cols-4 gap-2 text-sm px-2">
                    <div className="text-gray-600">PIC Commission</div>
                    <div className="text-right font-mono">{formatCurrency(picCalcResult.gross_comm)}</div>
                    <div className="text-right font-mono text-red-600">-{formatCurrency(picCalcResult.sst_amount)}</div>
                    <div className="text-right font-mono font-semibold text-blue-700">{formatCurrency(picCalcResult.nett_comm)}</div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-sm px-2">
                    <div className="text-gray-600">Trip Wallet</div>
                    <div className="text-right font-mono">{formatCurrency(tripCalcResult.gross_comm)}</div>
                    <div className="text-right font-mono text-red-600">-{formatCurrency(tripCalcResult.sst_amount)}</div>
                    <div className="text-right font-mono font-semibold text-purple-700">{formatCurrency(tripCalcResult.nett_comm)}</div>
                  </div>
                </>
              )}
              <Separator />
              <div className="grid grid-cols-4 gap-2 text-base font-bold px-2 py-1 bg-gray-50 rounded">
                <div>GRAND TOTAL</div>
                <div className="text-right font-mono">{formatCurrency(grandTotalGross)}</div>
                <div className="text-right font-mono text-red-600">-{formatCurrency(grandTotalSST)}</div>
                <div className="text-right font-mono text-blue-700" data-testid="total-after-sst">{formatCurrency(grandTotalNett)}</div>
              </div>
              {remainingPool > 0.01 && (
                <div className="flex justify-between text-sm text-orange-600 px-2">
                  <span>Remaining Unallocated from Pool</span>
                  <span className="font-mono font-semibold">{formatCurrency(remainingPool)}</span>
                </div>
              )}
              {remainingPool < -0.01 && (
                <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Over-allocated by {formatCurrency(Math.abs(remainingPool))}! Total exceeds commission pool.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ====== STEP 4: Documents ====== */}
      {currentStep === 4 && (
        <div className="space-y-4" data-testid="step-4-content">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Upload className="h-5 w-5" /> Supporting Documents</CardTitle>
              <CardDescription>
                Upload required documents for claim verification
                {!claimId && <span className="text-orange-600 ml-2">(Save as draft first to enable uploads)</span>}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <Label className="text-sm font-medium mb-3 block">Document Status Checklist</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['bank_lo', 'la', 'spa', 'sales_form', 'booking_fee', 'dp_slip'].map(item => (
                    <div key={item} className="flex items-center gap-2">
                      <Checkbox id={`check_${item}`} checked={formData.status_checklist.includes(item)} onCheckedChange={() => {
                        setFormData(prev => ({
                          ...prev,
                          status_checklist: prev.status_checklist.includes(item)
                            ? prev.status_checklist.filter(i => i !== item)
                            : [...prev.status_checklist, item],
                        }));
                      }} />
                      <Label htmlFor={`check_${item}`} className="text-sm capitalize">{item.replace(/_/g, ' ')}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {DOCUMENT_TYPES.map((docType) => {
                  const Icon = docType.icon;
                  const docs = uploadedDocs[docType.key] || [];
                  const isUploading = uploadProgress[docType.key] !== undefined;
                  return (
                    <div key={docType.key} className={`p-4 border rounded-lg ${docType.required && docs.length === 0 ? 'border-orange-200 bg-orange-50' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Icon className="h-5 w-5 text-gray-500" />
                          <span className="font-medium text-sm">{docType.label}</span>
                          {docType.required && <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">Required</Badge>}
                        </div>
                        {docs.length > 0 && <Badge className="bg-green-100 text-green-700 text-xs">{docs.length} file(s)</Badge>}
                      </div>
                      {docs.length > 0 && (
                        <div className="space-y-1 mb-3">
                          {docs.map((doc, idx) => (
                            <div key={idx} className="flex items-center justify-between p-1.5 bg-gray-50 rounded text-xs">
                              <div className="flex items-center gap-1.5"><FileText className="h-3 w-3 text-gray-400" /><span className="truncate max-w-[150px]">{doc.original_name}</span></div>
                              <CheckCircle className="h-3 w-3 text-green-500" />
                            </div>
                          ))}
                        </div>
                      )}
                      {isUploading && (
                        <div className="mb-3">
                          <Progress value={uploadProgress[docType.key]} className="h-1.5" />
                          <p className="text-xs text-gray-500 mt-1">Uploading... {uploadProgress[docType.key]}%</p>
                        </div>
                      )}
                      <input ref={(el) => (fileInputRefs.current[docType.key] = el)} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileSelect(docType.key)} />
                      <Button variant="outline" size="sm" className="w-full" onClick={() => fileInputRefs.current[docType.key]?.click()} disabled={!claimId || isUploading}>
                        <Upload className="h-4 w-4 mr-1" /> {docs.length > 0 ? 'Add More' : 'Upload'}
                      </Button>
                    </div>
                  );
                })}
              </div>

              <div className="space-y-2">
                <Label>Additional Remarks</Label>
                <Textarea value={formData.remark} onChange={(e) => handleInputChange('remark', e.target.value)} placeholder="Any additional notes or comments..." rows={3} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ====== STEP 5: Declaration & Submit ====== */}
      {currentStep === 5 && (
        <div className="space-y-4" data-testid="step-5-content">
          {/* Claim Summary Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5" /> Claim Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Entity</p>
                  <Badge className={isSamRumah ? 'bg-emerald-500' : 'bg-blue-500'}>{isSamRumah ? 'SAM Rumah' : 'SP SAM'}</Badge>
                </div>
                <div><p className="text-gray-500">Project</p><p className="font-semibold">{formData.project_name || '-'}</p></div>
                <div><p className="text-gray-500">Unit</p><p className="font-semibold">{formData.unit_number || '-'}</p></div>
                <div><p className="text-gray-500">Buyer</p><p className="font-semibold">{formData.customer_full_name || '-'}</p></div>
                <div><p className="text-gray-500">SPA Price</p><p className="font-semibold font-mono">{formatCurrency(formData.spa_price)}</p></div>
                <div><p className="text-gray-500">Commission Rate</p><p className="font-semibold">{formData.commission_percentage}%</p></div>
                <div><p className="text-gray-500">Total Commission</p><p className="font-semibold font-mono text-blue-700">{formatCurrency(totalGrossComm)}</p></div>
                <div><p className="text-gray-500">Net Payable</p><p className="font-semibold font-mono text-emerald-700">{formatCurrency(grandTotalNett)}</p></div>
              </div>

              <Separator />

              <div>
                <p className="font-medium text-sm mb-2">Commission Distribution (3-Part Calculation)</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="text-right">Rate %</TableHead>
                      <TableHead className="text-right">Gross (RM)</TableHead>
                      <TableHead className="text-right">SST 8%</TableHead>
                      {isSamRumah && <TableHead className="text-right">Plat 5%</TableHead>}
                      <TableHead className="text-right">Net (RM)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {managementChain.filter(m => m.rate_percentage > 0).map((m, idx) => (
                      <TableRow key={`mgmt-${idx}`}>
                        <TableCell className="font-medium">{m.name}</TableCell>
                        <TableCell><Badge variant="outline">{m.rank_display || m.rank?.toUpperCase()}</Badge></TableCell>
                        <TableCell className="text-right font-mono">{m.rate_percentage?.toFixed(2)}%</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(m.gross_comm)}</TableCell>
                        <TableCell className="text-right font-mono text-red-600">-{formatCurrency(m.sst_amount)}</TableCell>
                        {isSamRumah && <TableCell className="text-right font-mono text-amber-600">-{formatCurrency(m.platform_fee)}</TableCell>}
                        <TableCell className="text-right font-mono font-semibold">{formatCurrency(m.nett_comm)}</TableCell>
                      </TableRow>
                    ))}
                    {isSamRumah && formData.is_cobroke && cobrokeChain.filter(m => m.rate_percentage > 0).map((m, idx) => (
                      <TableRow key={`cb-${idx}`}>
                        <TableCell className="font-medium">{m.name}</TableCell>
                        <TableCell><Badge variant="outline" className="bg-emerald-50">{m.level_display || m.rank_display}</Badge></TableCell>
                        <TableCell className="text-right font-mono">{m.rate_percentage?.toFixed(2)}%</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(m.gross_comm)}</TableCell>
                        <TableCell className="text-right font-mono text-red-600">-{formatCurrency(m.sst_amount)}</TableCell>
                        {isSamRumah && <TableCell className="text-right font-mono text-amber-600">-{formatCurrency(m.platform_fee)}</TableCell>}
                        <TableCell className="text-right font-mono font-semibold text-emerald-700">{formatCurrency(m.nett_comm)}</TableCell>
                      </TableRow>
                    ))}
                    {!isSamRumah && picCalcResult.gross_comm > 0 && (
                      <TableRow>
                        <TableCell className="font-medium">{formData.pic_user_name || 'PIC'}</TableCell>
                        <TableCell><Badge variant="outline" className="bg-blue-50">PIC</Badge></TableCell>
                        <TableCell className="text-right font-mono">{formData.pic_commission_percentage?.toFixed(2)}%</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(picCalcResult.gross_comm)}</TableCell>
                        <TableCell className="text-right font-mono text-red-600">-{formatCurrency(picCalcResult.sst_amount)}</TableCell>
                        <TableCell className="text-right font-mono font-semibold text-blue-700">{formatCurrency(picCalcResult.nett_comm)}</TableCell>
                      </TableRow>
                    )}
                    {!isSamRumah && tripCalcResult.gross_comm > 0 && (
                      <TableRow>
                        <TableCell className="font-medium">Trip Wallet</TableCell>
                        <TableCell><Badge variant="outline" className="bg-purple-50">TRIP</Badge></TableCell>
                        <TableCell className="text-right font-mono">{formData.trip_wallet_percentage?.toFixed(2)}%</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(tripCalcResult.gross_comm)}</TableCell>
                        <TableCell className="text-right font-mono text-red-600">-{formatCurrency(tripCalcResult.sst_amount)}</TableCell>
                        <TableCell className="text-right font-mono font-semibold text-purple-700">{formatCurrency(tripCalcResult.nett_comm)}</TableCell>
                      </TableRow>
                    )}
                    <TableRow className="bg-gray-100 font-bold">
                      <TableCell colSpan={3}>GRAND TOTAL</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(grandTotalGross)}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">-{formatCurrency(grandTotalSST)}</TableCell>
                      {isSamRumah && <TableCell className="text-right font-mono text-amber-600">-{formatCurrency(totalAgentPlatform + totalCobrokePlatform)}</TableCell>}
                      <TableCell className="text-right font-mono text-lg text-blue-700">{formatCurrency(grandTotalNett)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Declaration */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Shield className="h-5 w-5" /> Declaration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700 leading-relaxed">
                I, the undersigned, hereby declare that all information provided in this commission claim form is true, accurate, and complete to the best of my knowledge. I understand that any false or misleading information may result in the rejection of this claim and/or disciplinary action.
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Checkbox id="decl_accurate" checked={declarations.accurate} onCheckedChange={(c) => setDeclarations(prev => ({ ...prev, accurate: c }))} data-testid="decl-accurate" />
                  <Label htmlFor="decl_accurate" className="text-sm">I declare that all information provided is accurate and complete.</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox id="decl_commission" checked={declarations.commission} onCheckedChange={(c) => setDeclarations(prev => ({ ...prev, commission: c }))} data-testid="decl-commission" />
                  <Label htmlFor="decl_commission" className="text-sm">I agree to the commission distribution as outlined above.</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox id="decl_documents" checked={declarations.documents} onCheckedChange={(c) => setDeclarations(prev => ({ ...prev, documents: c }))} data-testid="decl-documents" />
                  <Label htmlFor="decl_documents" className="text-sm">I confirm all supporting documents are genuine and unaltered.</Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Approval Flow Info */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-2">
                <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800 text-sm">Approval Flow</p>
                  <p className="text-blue-700 text-xs mt-1">
                    Agent Submit &#8594; PIC Review &#8594; Admin Review &#8594; Account Final Approval &#8594; Payment
                  </p>
                  <p className="text-blue-600 text-xs mt-1">
                    If revision is needed at any stage, the claim will be returned to you for correction and resubmission.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Navigation & Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div>
          {currentStep > 1 && (
            <Button variant="outline" onClick={() => setCurrentStep(prev => prev - 1)} data-testid="prev-step-btn">
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {onCancel && (
            <Button variant="outline" onClick={onCancel}><X className="h-4 w-4 mr-1" /> Cancel</Button>
          )}
          <Button variant="outline" onClick={handleSaveDraft} disabled={saving} data-testid="save-draft-btn">
            <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Save Draft')}
          </Button>
          {currentStep < 5 ? (
            <Button onClick={() => setCurrentStep(prev => prev + 1)} className="bg-blue-600 hover:bg-blue-700" data-testid="next-step-btn">
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading || !declarations.accurate || !declarations.commission || !declarations.documents} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-claim-btn">
              <Send className="h-4 w-4 mr-1" /> {loading ? 'Submitting...' : (isRevision ? 'Resubmit for Review' : 'Submit for Review')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommissionClaimForm;
